/**
 * Suppress unhandled promise rejections from mapbox-gl's session auth tracker
 * when the map is destroyed while a session API request is in flight.
 *
 * mapbox-gl's internal session-authentication class stores the caller's error
 * callback as `this.errorCb`. Its `remove()` method (called by `map.remove()`)
 * sets `this.errorCb = null` to release the reference. However, if a session
 * API fetch is already in flight when `remove()` runs, the fetch's `.catch`
 * handler eventually calls back into `processRequests()`, which tries to invoke
 * `this.errorCb(error)` — now `null` — throwing a TypeError that becomes an
 * unhandled promise rejection:
 *
 *   TypeError: Unhandled Promise Rejection: this.errorCb is not a function
 *
 * This is a bug in mapbox-gl (present at least in v3.29.0): the class does not
 * guard its own async callbacks against post-`remove()` nullification. The
 * rejection cannot be caught at the `map.remove()` call site because it is
 * asynchronous — it fires after `remove()` has already returned.
 *
 * The fix intercepts the `unhandledrejection` window event and discards it when
 * the reason matches the exact TypeError message emitted by mapbox-gl's session
 * tracking code, preventing spurious Sentry noise without masking any other
 * mapbox or application errors.
 */
export function suppressMapboxSessionErrors(): void {
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const { reason } = event;

    if (
      reason instanceof TypeError
      && reason.message === 'this.errorCb is not a function'
    ) {
      event.preventDefault();
    }
  });
}
